import * as z from 'zod';

export const QueryPaymentLinkPaymentsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryPaymentLinkPaymentsResponse = /*#__PURE__*/ z.object({
  paymentLinkPayments: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe(
              'Payment link payment ID. When a gift card is used, this is the gift card payment ID.\nOtherwise, this is the transaction ID associated with the relevant payment type.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the payment link payment is updated.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the payment was created.')
            .optional()
            .nullable(),
          amount: z.string().describe('Payment amount.').optional(),
          paymentLinkId: z
            .string()
            .describe('Payment link ID associated with the payment.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          receiptId: z
            .string()
            .describe(
              'Receipt ID associated with the payment, available in the Get Paid Receipts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['UNKNOWN_PAYMENT_TYPE', 'REGULAR', 'GIFT_CARD'])
            .describe('Payment type.')
            .optional(),
          currency: z
            .string()
            .describe(
              'Currency code in [ISO-4217 alphabetic](https://www.iso.org/iso-4217-currency-codes.html) format.'
            )
            .optional(),
          transactionDate: z
            .date()
            .describe('Date and time the payment transaction was created.')
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the payment link.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.intersection(
          z.xor([
            z.object({
              regularPaymentLinkPayment: z.never().optional(),
              giftCardPaymentLinkPayment: z.never().optional(),
            }),
            z.object({
              giftCardPaymentLinkPayment: z.never().optional(),
              regularPaymentLinkPayment: z
                .intersection(
                  z.object({
                    paymentMethod: z
                      .string()
                      .describe('Payment method.')
                      .min(1)
                      .max(100)
                      .optional(),
                    paymentProvider: z
                      .string()
                      .describe('Payment service provider.')
                      .min(1)
                      .max(100)
                      .optional(),
                    contactDetails: z
                      .object({
                        firstName: z
                          .string()
                          .describe("Contact's first name.")
                          .optional()
                          .nullable(),
                        lastName: z
                          .string()
                          .describe("Contact's last name.")
                          .optional()
                          .nullable(),
                        phone: z
                          .string()
                          .describe("Contact's phone number.")
                          .optional()
                          .nullable(),
                        company: z
                          .string()
                          .describe("Contact's company name.")
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe('Email associated with the address.')
                          .email()
                          .optional()
                          .nullable(),
                        vatId: z
                          .object({
                            _id: z
                              .string()
                              .describe("Customer's tax ID.")
                              .max(100)
                              .optional(),
                            type: z
                              .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                              .describe(
                                'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                              )
                              .optional(),
                          })
                          .describe('Tax info.')
                          .optional(),
                      })
                      .describe('Buyer contact details.')
                      .optional(),
                    contactId: z
                      .string()
                      .describe('Buyer contact ID, if relevant.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    billingAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe("Buyer's billing address.")
                      .optional(),
                    method: z
                      .enum(['UNKNOWN_METHOD_DATA', 'CREDIT_CARD', 'WALLET'])
                      .describe('Payment method type.')
                      .optional(),
                    order: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['UNKNOWN_ORDER_TYPE', 'ECOM', 'CASHIER'])
                            .describe('Payment order type.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            ecomOrder: z.never().optional(),
                            cashierOrder: z.never().optional(),
                          }),
                          z.object({
                            cashierOrder: z.never().optional(),
                            ecomOrder: z
                              .object({
                                ecomOrderId: z
                                  .string()
                                  .describe('Wix eCommerce order ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe('Wix eCommerce order details.'),
                          }),
                          z.object({
                            ecomOrder: z.never().optional(),
                            cashierOrder: z
                              .object({
                                cashierOrderId: z
                                  .string()
                                  .describe('Wix Cashier payment order ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe('Wix Cashier order details.'),
                          }),
                        ])
                      )
                      .describe('Payment order.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      creditCardPaymentMethodData: z.never().optional(),
                      walletPaymentMethodData: z.never().optional(),
                    }),
                    z.object({
                      walletPaymentMethodData: z.never().optional(),
                      creditCardPaymentMethodData: z
                        .object({
                          network: z
                            .string()
                            .describe('Card network.')
                            .min(1)
                            .max(100)
                            .optional(),
                          maskedCreditCard: z
                            .string()
                            .describe('Masked card number.')
                            .max(100)
                            .optional(),
                          holderName: z
                            .string()
                            .describe('Card holder name.')
                            .max(100)
                            .optional(),
                        })
                        .describe('Credit card payment method data.'),
                    }),
                    z.object({
                      creditCardPaymentMethodData: z.never().optional(),
                      walletPaymentMethodData: z
                        .object({
                          buyerAccountEmail: z
                            .string()
                            .describe('Buyer account email.')
                            .email()
                            .optional()
                            .nullable(),
                        })
                        .describe('Wallet payment method data.'),
                    }),
                  ])
                )
                .describe(
                  "Conventional payment, not including Wix eCommerce's gift cards. Includes payments made through Wix Payments, Stripe, and all [supported payment gateways](https://www.wix.com/payments/payment-gateways)."
                ),
            }),
            z.object({
              regularPaymentLinkPayment: z.never().optional(),
              giftCardPaymentLinkPayment: z
                .object({
                  ecomOrderId: z
                    .string()
                    .describe(
                      'Wix eCommerce order ID associated with the gift card payment.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe("Contact's first name.")
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe("Contact's company name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe('Email associated with the address.')
                        .email()
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .max(100)
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe('Tax info.')
                        .optional(),
                    })
                    .describe('Buyer contact details.')
                    .optional(),
                  contactId: z
                    .string()
                    .describe('Buyer contact ID, if relevant.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  billingAddress: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe("Buyer's billing address.")
                    .optional(),
                })
                .describe('Wix eCommerce gift card payment details.'),
            }),
          ]),
          z.xor([
            z.object({
              ecomCheckout: z.never().optional(),
              ecomOrderCheckout: z.never().optional(),
            }),
            z.object({
              ecomOrderCheckout: z.never().optional(),
              ecomCheckout: z
                .object({
                  ecomCheckoutId: z
                    .string()
                    .describe('Wix eCommerce checkout ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe('Wix eCommerce checkout.'),
            }),
            z.object({
              ecomCheckout: z.never().optional(),
              ecomOrderCheckout: z
                .object({
                  orderPaymentRequestId: z
                    .string()
                    .describe('Wix eCommerce order payment request ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe('Wix eCommerce order checkout.'),
            }),
          ])
        )
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const SearchPaymentLinkPaymentsRequest = /*#__PURE__*/ z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Aggregation name, returned in `aggregations.results.name`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                  )
                  .max(200)
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe('Sort type.')
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe('Sort direction.')
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe(
                              'Options for including missing values in results.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not specified.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not specified.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe('Operator type for the scalar aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe('Sort type.')
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe('Sort direction.')
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values in results.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe(
                                    "A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status."
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not specified.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not specified.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe(
                                    'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Operator type for the scalar aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe(
                      'A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.'
                    ),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.\nApplies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Search options.'),
});
export const SearchPaymentLinkPaymentsResponse = /*#__PURE__*/ z.object({
  paymentLinkPayments: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe(
              'Payment link payment ID. When a gift card is used, this is the gift card payment ID.\nOtherwise, this is the transaction ID associated with the relevant payment type.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the payment link payment is updated.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the payment was created.')
            .optional()
            .nullable(),
          amount: z.string().describe('Payment amount.').optional(),
          paymentLinkId: z
            .string()
            .describe('Payment link ID associated with the payment.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          receiptId: z
            .string()
            .describe(
              'Receipt ID associated with the payment, available in the Get Paid Receipts API.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['UNKNOWN_PAYMENT_TYPE', 'REGULAR', 'GIFT_CARD'])
            .describe('Payment type.')
            .optional(),
          currency: z
            .string()
            .describe(
              'Currency code in [ISO-4217 alphabetic](https://www.iso.org/iso-4217-currency-codes.html) format.'
            )
            .optional(),
          transactionDate: z
            .date()
            .describe('Date and time the payment transaction was created.')
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the payment link.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.intersection(
          z.xor([
            z.object({
              regularPaymentLinkPayment: z.never().optional(),
              giftCardPaymentLinkPayment: z.never().optional(),
            }),
            z.object({
              giftCardPaymentLinkPayment: z.never().optional(),
              regularPaymentLinkPayment: z
                .intersection(
                  z.object({
                    paymentMethod: z
                      .string()
                      .describe('Payment method.')
                      .min(1)
                      .max(100)
                      .optional(),
                    paymentProvider: z
                      .string()
                      .describe('Payment service provider.')
                      .min(1)
                      .max(100)
                      .optional(),
                    contactDetails: z
                      .object({
                        firstName: z
                          .string()
                          .describe("Contact's first name.")
                          .optional()
                          .nullable(),
                        lastName: z
                          .string()
                          .describe("Contact's last name.")
                          .optional()
                          .nullable(),
                        phone: z
                          .string()
                          .describe("Contact's phone number.")
                          .optional()
                          .nullable(),
                        company: z
                          .string()
                          .describe("Contact's company name.")
                          .optional()
                          .nullable(),
                        email: z
                          .string()
                          .describe('Email associated with the address.')
                          .email()
                          .optional()
                          .nullable(),
                        vatId: z
                          .object({
                            _id: z
                              .string()
                              .describe("Customer's tax ID.")
                              .max(100)
                              .optional(),
                            type: z
                              .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                              .describe(
                                'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                              )
                              .optional(),
                          })
                          .describe('Tax info.')
                          .optional(),
                      })
                      .describe('Buyer contact details.')
                      .optional(),
                    contactId: z
                      .string()
                      .describe('Buyer contact ID, if relevant.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    billingAddress: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe("Buyer's billing address.")
                      .optional(),
                    method: z
                      .enum(['UNKNOWN_METHOD_DATA', 'CREDIT_CARD', 'WALLET'])
                      .describe('Payment method type.')
                      .optional(),
                    order: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['UNKNOWN_ORDER_TYPE', 'ECOM', 'CASHIER'])
                            .describe('Payment order type.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            ecomOrder: z.never().optional(),
                            cashierOrder: z.never().optional(),
                          }),
                          z.object({
                            cashierOrder: z.never().optional(),
                            ecomOrder: z
                              .object({
                                ecomOrderId: z
                                  .string()
                                  .describe('Wix eCommerce order ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe('Wix eCommerce order details.'),
                          }),
                          z.object({
                            ecomOrder: z.never().optional(),
                            cashierOrder: z
                              .object({
                                cashierOrderId: z
                                  .string()
                                  .describe('Wix Cashier payment order ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe('Wix Cashier order details.'),
                          }),
                        ])
                      )
                      .describe('Payment order.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      creditCardPaymentMethodData: z.never().optional(),
                      walletPaymentMethodData: z.never().optional(),
                    }),
                    z.object({
                      walletPaymentMethodData: z.never().optional(),
                      creditCardPaymentMethodData: z
                        .object({
                          network: z
                            .string()
                            .describe('Card network.')
                            .min(1)
                            .max(100)
                            .optional(),
                          maskedCreditCard: z
                            .string()
                            .describe('Masked card number.')
                            .max(100)
                            .optional(),
                          holderName: z
                            .string()
                            .describe('Card holder name.')
                            .max(100)
                            .optional(),
                        })
                        .describe('Credit card payment method data.'),
                    }),
                    z.object({
                      creditCardPaymentMethodData: z.never().optional(),
                      walletPaymentMethodData: z
                        .object({
                          buyerAccountEmail: z
                            .string()
                            .describe('Buyer account email.')
                            .email()
                            .optional()
                            .nullable(),
                        })
                        .describe('Wallet payment method data.'),
                    }),
                  ])
                )
                .describe(
                  "Conventional payment, not including Wix eCommerce's gift cards. Includes payments made through Wix Payments, Stripe, and all [supported payment gateways](https://www.wix.com/payments/payment-gateways)."
                ),
            }),
            z.object({
              regularPaymentLinkPayment: z.never().optional(),
              giftCardPaymentLinkPayment: z
                .object({
                  ecomOrderId: z
                    .string()
                    .describe(
                      'Wix eCommerce order ID associated with the gift card payment.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  contactDetails: z
                    .object({
                      firstName: z
                        .string()
                        .describe("Contact's first name.")
                        .optional()
                        .nullable(),
                      lastName: z
                        .string()
                        .describe("Contact's last name.")
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe("Contact's phone number.")
                        .optional()
                        .nullable(),
                      company: z
                        .string()
                        .describe("Contact's company name.")
                        .optional()
                        .nullable(),
                      email: z
                        .string()
                        .describe('Email associated with the address.')
                        .email()
                        .optional()
                        .nullable(),
                      vatId: z
                        .object({
                          _id: z
                            .string()
                            .describe("Customer's tax ID.")
                            .max(100)
                            .optional(),
                          type: z
                            .enum(['UNSPECIFIED', 'CPF', 'CNPJ'])
                            .describe(
                              'Tax type.\n\nSupported values:\n+ `CPF`: for individual tax payers\n+ `CNPJ`: for corporations'
                            )
                            .optional(),
                        })
                        .describe('Tax info.')
                        .optional(),
                    })
                    .describe('Buyer contact details.')
                    .optional(),
                  contactId: z
                    .string()
                    .describe('Buyer contact ID, if relevant.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  billingAddress: z
                    .object({
                      city: z.string().optional().nullable(),
                      subdivision: z.string().optional().nullable(),
                      country: z.string().optional().nullable(),
                      postalCode: z.string().optional().nullable(),
                      addressLine1: z.string().optional().nullable(),
                      addressLine2: z.string().optional().nullable(),
                    })
                    .describe("Buyer's billing address.")
                    .optional(),
                })
                .describe('Wix eCommerce gift card payment details.'),
            }),
          ]),
          z.xor([
            z.object({
              ecomCheckout: z.never().optional(),
              ecomOrderCheckout: z.never().optional(),
            }),
            z.object({
              ecomOrderCheckout: z.never().optional(),
              ecomCheckout: z
                .object({
                  ecomCheckoutId: z
                    .string()
                    .describe('Wix eCommerce checkout ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe('Wix eCommerce checkout.'),
            }),
            z.object({
              ecomCheckout: z.never().optional(),
              ecomOrderCheckout: z
                .object({
                  orderPaymentRequestId: z
                    .string()
                    .describe('Wix eCommerce order payment request ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                })
                .describe('Wix eCommerce order checkout.'),
            }),
          ])
        )
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'Aggregation name, returned in `aggregations.results.name`.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Aggregation type. Must align with the corresponding aggregation field.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Total number of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe('Aggregation type.')
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field which the data was aggregated by.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Number of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Total number of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Response aggregation data.')
    .optional(),
});
export const IssueReceiptRequest = /*#__PURE__*/ z.object({
  paymentLinkPaymentId: z
    .string()
    .describe('Payment link payment ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const IssueReceiptResponse = /*#__PURE__*/ z.object({
  receiptId: z
    .string()
    .describe('Issued receipt ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
